import { INetworkResult } from '@/coreDomainsModule/networkResult';
import {
  OrderTemplateApi,
  OrderTemplateDetailResult,
  OrderTemplateListResult,
  OrderTemplatePresignedDownloadResult,
  OrderTemplatePresignedUploadRequest,
  OrderTemplatePresignedUploadResult,
  OrderTemplateUpsertRequest,
  SuccessResponse,
} from '../axios/generated/orderManage/api';
import { IOrderTemplateRepository } from '../interfaces/IOrderTemplateRepository';
import { IError } from '../interfaces/IError';
import { axiosErrorHandler } from '../axios/axiosError';
import { customAxiosInstance } from '../axios/axiosInstance';

/**
 * 発注テンプレートAPIを呼び出すRepository
 */
export class OrderTemplateRepository implements IOrderTemplateRepository {
  /**
   * テンプレート一覧を取得する
   */
  public async getList(): Promise<INetworkResult<OrderTemplateListResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new OrderTemplateApi(undefined, undefined, axiosInstance);

    try {
      const response = await api.getOrderTemplateList();
      if (response.status === 200) {
        return { status: { type: 'success', data: response.data.result } };
      }
      throw new Error('想定外のレスポンス');
    } catch (error) {
      return { status: { type: 'error', error: axiosErrorHandler(error) } };
    }
  }

  /**
   * テンプレート詳細を取得する
   * @param templateId - テンプレートID
   */
  public async getDetail(templateId: string): Promise<INetworkResult<OrderTemplateDetailResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new OrderTemplateApi(undefined, undefined, axiosInstance);

    try {
      const response = await api.getOrderTemplate(templateId);
      if (response.status === 200) {
        return { status: { type: 'success', data: response.data.result } };
      }
      throw new Error('想定外のレスポンス');
    } catch (error) {
      return { status: { type: 'error', error: axiosErrorHandler(error) } };
    }
  }

  /**
   * テンプレートを新規登録する
   * @param request - 登録内容
   */
  public async create(request: OrderTemplateUpsertRequest): Promise<INetworkResult<OrderTemplateDetailResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new OrderTemplateApi(undefined, undefined, axiosInstance);

    try {
      const response = await api.createOrderTemplate(request);
      if (response.status === 200) {
        return { status: { type: 'success', data: response.data.result } };
      }
      throw new Error('想定外のレスポンス');
    } catch (error) {
      return { status: { type: 'error', error: axiosErrorHandler(error) } };
    }
  }

  /**
   * テンプレートを更新する
   * @param templateId - テンプレートID
   * @param request - 更新内容
   */
  public async update(templateId: string, request: OrderTemplateUpsertRequest): Promise<INetworkResult<OrderTemplateDetailResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new OrderTemplateApi(undefined, undefined, axiosInstance);

    try {
      const response = await api.updateOrderTemplate(templateId, request);
      if (response.status === 200) {
        return { status: { type: 'success', data: response.data.result } };
      }
      throw new Error('想定外のレスポンス');
    } catch (error) {
      return { status: { type: 'error', error: axiosErrorHandler(error) } };
    }
  }

  /**
   * テンプレートを削除する
   * @param templateId - テンプレートID
   */
  public async delete(templateId: string): Promise<INetworkResult<SuccessResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new OrderTemplateApi(undefined, undefined, axiosInstance);

    try {
      const response = await api.deleteOrderTemplate(templateId);
      if (response.status === 200) {
        return { status: { type: 'success', data: response.data } };
      }
      throw new Error('想定外のレスポンス');
    } catch (error) {
      return { status: { type: 'error', error: axiosErrorHandler(error) } };
    }
  }

  /**
   * アップロード用のPresigned URLを発行する
   * @param request - アップロード条件
   */
  public async createUploadUrl(
    request: OrderTemplatePresignedUploadRequest
  ): Promise<INetworkResult<OrderTemplatePresignedUploadResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new OrderTemplateApi(undefined, undefined, axiosInstance);

    try {
      const response = await api.createOrderTemplateUploadUrl(request);
      if (response.status === 200) {
        return { status: { type: 'success', data: response.data.result } };
      }
      throw new Error('想定外のレスポンス');
    } catch (error) {
      return { status: { type: 'error', error: axiosErrorHandler(error) } };
    }
  }

  /**
   * ダウンロード用のPresigned URLを取得する
   * @param templateId - テンプレートID
   */
  public async getDownloadUrl(templateId: string): Promise<INetworkResult<OrderTemplatePresignedDownloadResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new OrderTemplateApi(undefined, undefined, axiosInstance);

    try {
      const response = await api.getOrderTemplateDownloadUrl(templateId);
      if (response.status === 200) {
        return { status: { type: 'success', data: response.data.result } };
      }
      throw new Error('想定外のレスポンス');
    } catch (error) {
      return { status: { type: 'error', error: axiosErrorHandler(error) } };
    }
  }
}
